import {
  CardState,
  DoughnutChartData,
  InlineTableData,
  PerfTableColumn,
  ProductReturnPerfRow,
} from "../data-visualization";

// Initial/loading state; dashboard.component.ts mutates these in place once data lands.

export enum ReturnsCardIndex {
  ReturnRequests = 0,
  RequestedReturnValue = 1,
  ReturnsDeflected = 2,
  CreditsIssued = 3,
}

// Looked up by key instead of array position - reordering subMetrics below can't mislabel a value.
export enum ReturnsKpiSubMetricKey {
  Completed = "completed",
  Deflected = "deflected",
  Abandoned = "abandoned",
  AvgRequest = "avg_request",
  RevenueAbandoned = "revenue_abandoned",
  DeflectionRate = "deflection_rate",
  RevenueSaved = "revenue_saved",
  CreditValue = "credit_value",
  AvgCredit = "avg_credit",
}

export interface ReturnsKpiSubMetric {
  key: ReturnsKpiSubMetricKey;
  label: string;
  value: string;
}

export interface ReturnsKpiCard {
  title: string;
  hint?: string;
  value: string;
  percentage: number | null;
  is_negative: boolean;
  previousValueLabel?: string | null;
  subMetrics: ReturnsKpiSubMetric[];
}

export interface ReturnsActivityStat {
  icon: string;
  iconBg: string;
  label: string;
  count: string;
  value: string;
  hint?: string;
}

export interface ReturnsTableRow {
  name: string;
  requests: string;
  value: string;
  deflection?: string;
}

export interface ReturnsOfferRow {
  name: string;
  presented: string;
  accepted: string;
  acceptance: string;
  revenueSaved: string;
}

// Per-row icon layout doesn't match ai-insights-card's shared component, so this gets its own shape.
export interface ReturnsAiInsight {
  icon: string;
  iconBg: string;
  text: string;
}

export const returnsTopCards: ReturnsKpiCard[] = [
  {
    title: "Total Return Requests",
    hint: "Return requests received during the selected period, including completed, deflected, and abandoned requests.",
    value: "--",
    percentage: null,
    is_negative: false,
    subMetrics: [
      { key: ReturnsKpiSubMetricKey.Completed, label: "Completed", value: "--" },
      { key: ReturnsKpiSubMetricKey.Deflected, label: "Deflected", value: "--" },
      { key: ReturnsKpiSubMetricKey.Abandoned, label: "Abandoned", value: "--" },
    ],
  },
  {
    title: "Requested Return Value",
    hint: "The total dollar value of items included in return requests during the selected period.",
    value: "--",
    percentage: null,
    is_negative: false,
    subMetrics: [
      { key: ReturnsKpiSubMetricKey.AvgRequest, label: "Avg. Request", value: "--" },
      { key: ReturnsKpiSubMetricKey.RevenueAbandoned, label: "Revenue Abandoned", value: "--" },
    ],
  },
  {
    title: "Returns Deflected",
    hint: "Return requests where the customer was retained instead of completing the return.",
    value: "--",
    percentage: null,
    is_negative: false,
    subMetrics: [
      { key: ReturnsKpiSubMetricKey.DeflectionRate, label: "Deflection Rate", value: "--" },
      { key: ReturnsKpiSubMetricKey.RevenueSaved, label: "Revenue Saved", value: "--" },
    ],
  },
  {
    title: "Credits Issued",
    hint: "Store credits issued in place of a refund for a return request.",
    value: "--",
    percentage: null,
    is_negative: false,
    subMetrics: [
      { key: ReturnsKpiSubMetricKey.CreditValue, label: "Credit Value", value: "--" },
      { key: ReturnsKpiSubMetricKey.AvgCredit, label: "Avg. Credit", value: "--" },
    ],
  },
];

/* Return request outcomes donut */
export const returnsOutcomesData: DoughnutChartData = {
  data: [],
  noData: true,
  labels: ["Completed", "Deflected", "Abandoned"],
  loading: false,
  total: "0",
  backgroundColors: [
    {
      backgroundColor: ["#8AE5FF", "#0CBE68", "#CBD5E1"],
    },
  ],
};

export const returnsOutcomesLegend: InlineTableData = [
  { name: "Completed", color: "#8AE5FF", value: "--", percentage: "--" },
  { name: "Deflected", color: "#0CBE68", value: "--", percentage: "--" },
  { name: "Abandoned", color: "#CBD5E1", value: "--", percentage: "--" },
];

/* Open Return Activity */
export const returnsActivityStats: ReturnsActivityStat[] = [
  {
    icon: "/assets/IconsLibrary/pending-returns-icon.svg",
    iconBg: "#E5EDFF",
    label: "Pending Returns",
    count: "--",
    value: "--",
    hint: "Return requests that are still awaiting processing.",
  },
  {
    icon: "/assets/IconsLibrary/expired-returns-icon.svg",
    iconBg: "rgba(217, 83, 79, 0.1)",
    label: "Expired Returns",
    count: "--",
    value: "--",
    hint: "Return requests where the return window elapsed before completion.",
  },
];

export const returnsReasons: CardState<ReturnsTableRow> = {
  data: [],
  loading: false,
  noData: true,
};

export const returnsReasonsNameColumn: PerfTableColumn = { label: "Reason", field: "name" };
export const returnsReasonsValueColumns: PerfTableColumn[] = [
  { label: "Requests", field: "requests" },
  { label: "Value", field: "value" },
];

export const returnsProducts: CardState<ProductReturnPerfRow> = {
  data: [],
  loading: false,
  noData: true,
};

export const returnsProductsNameColumn: PerfTableColumn = { label: "Product", field: "name" };
export const returnsProductsValueColumns: PerfTableColumn[] = [
  { label: "Requests", field: "requests" },
  { label: "Value", field: "value" },
  { label: "Deflection", field: "deflectionRate" },
];

export const returnsOffers: CardState<ReturnsOfferRow> = {
  data: [],
  loading: false,
  noData: true,
};

export const returnsOffersNameColumn: PerfTableColumn = { label: "Offer", field: "name" };
export const returnsOffersValueColumns: PerfTableColumn[] = [
  { label: "Presented", field: "presented" },
  { label: "Accepted", field: "accepted" },
  { label: "Acceptance", field: "acceptance" },
  { label: "Revenue Saved", field: "revenueSaved" },
];

export const returnsAiInsights: CardState<ReturnsAiInsight> = {
  data: [],
  loading: false,
  noData: true,
};

export const unlock360Subtitle =
  "Shipment-to-return ratio: returned units as a share of eligible shipped or delivered units.";

export const returnsHints = {
  outcomes: "How return requests were resolved during the selected period.",
  openActivity: "Return requests that haven't reached a final outcome yet.",
  reasons: "The most common reasons customers gave for requesting a return.",
  products: "The products with the most return requests, and how many were deflected.",
  offers: "Performance of retention offers presented to customers during a return request.",
  aiInsights: "AI-generated highlights surfaced from your account's recent return activity.",
};
